import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import PointsModal from '../../components/PointsModal';
import SpinGrantModal from '../../components/SpinGrantModal';

const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtTs(isoString) {
  const d = new Date(isoString);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const h = d.getHours(), m = d.getMinutes();
  const hr = h % 12 || 12;
  const ap = h < 12 ? 'am' : 'pm';
  const time = m === 0 ? `${hr}${ap}` : `${hr}:${String(m).padStart(2, '0')}${ap}`;
  const dateLabel = isToday ? 'Today' : isYesterday ? 'Yesterday' : `${d.getDate()} ${MON[d.getMonth()]}`;
  return { dateLabel, time };
}

export default function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();
  const [user, setUser] = useState(null);
  const [salesStats, setSalesStats] = useState(null);
  const [showPoints, setShowPoints] = useState(false);
  const [showSpin, setShowSpin] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showGiftCoupon, setShowGiftCoupon] = useState(false);
  const [giftCouponDetail, setGiftCouponDetail] = useState('1g Nug - Free Pickup');
  const [resetLink, setResetLink] = useState(null);
  const [showRename, setShowRename] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [showXpAdjust, setShowXpAdjust] = useState(false);
  const [xpAmount, setXpAmount] = useState('');
  const [xpReason, setXpReason] = useState('');
  const [xpHistory, setXpHistory] = useState([]);
  const [showDeleteCouponConfirm, setShowDeleteCouponConfirm] = useState(false);
  const [deleteCouponTarget, setDeleteCouponTarget] = useState(null);

  const fetchUser = () => {
    api.get(`/users/${id}`).then(setUser).catch(() => {});
    api.get(`/admin/sales/by-user/${id}`).then(setSalesStats).catch(() => {});
    api.get(`/admin/users/${id}/xp-history`).then(setXpHistory).catch(() => {});
  };

  useEffect(fetchUser, [id]);

  const handleDelete = async () => {
    try {
      const data = await api.delete(`/admin/users/${id}`);
      showToast('success', data.message);
      setShowDeleteConfirm(false);
      setTimeout(() => navigate('/admin'), 1500);
    } catch (err) {
      showToast('error', err.message);
      setShowDeleteConfirm(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      const data = await api.post(`/admin/users/${id}/reset-password`);
      setResetLink(data.resetUrl);
    } catch (err) {
      showToast('error', err.message);
    }
  };

  const handleRedeemCoupon = async (couponId) => {
    try {
      await api.put(`/admin/coupons/${couponId}/redeem`);
      showToast('success', 'Coupon marked as picked up');
      fetchUser();
    } catch (err) {
      showToast('error', err.message);
    }
  };

  const handleRename = async () => {
    try {
      const data = await api.put(`/admin/users/${id}/rename`, { username: newUsername });
      showToast('success', `Renamed to "${data.new_username}"`);
      setShowRename(false);
      fetchUser();
    } catch (err) {
      showToast('error', err.message);
    }
  };

  const handleDeleteCoupon = async () => {
    try {
      await api.delete(`/admin/coupons/${deleteCouponTarget.id}`);
      showToast('success', `Coupon ${deleteCouponTarget.coupon_code} deleted`);
      setShowDeleteCouponConfirm(false);
      setDeleteCouponTarget(null);
      fetchUser();
    } catch (err) {
      showToast('error', err.message);
      setShowDeleteCouponConfirm(false);
      setDeleteCouponTarget(null);
    }
  };

  const handleXpAdjust = async () => {
    try {
      await api.post(`/admin/users/${id}/adjust-xp`, { amount: parseInt(xpAmount), reason: xpReason });
      showToast('success', `XP adjusted by ${xpAmount}`);
      setShowXpAdjust(false);
      setXpAmount('');
      setXpReason('');
      fetchUser();
    } catch (err) {
      showToast('error', err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('success', 'Link copied to clipboard!');
    });
  };

  if (!user) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <div>
      <Link to="/admin" style={{ fontSize: 14, marginBottom: 12, display: 'inline-block' }}>
        &larr; Back to Grow Room
      </Link>

      <h1 style={{ marginBottom: 20 }}>
        {user.username}
        {user.deleted_at && <span className="badge badge-redeemed" style={{ marginLeft: 8 }}>Deleted</span>}
      </h1>

<div className="stat-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-value">{user.points}</div>
          <div className="stat-label">Points</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{user.spin_tokens}</div>
          <div className="stat-label">Spin Tokens</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{user.total_rewards || 0}</div>
          <div className="stat-label">Total Rewards</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--accent)' }}>{user.level || 1}</div>
          <div className="stat-label">Level</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--accent)' }}>{(user.xp || 0).toLocaleString()}</div>
          <div className="stat-label">XP</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--success)' }}>+{user.points_today || 0}</div>
          <div className="stat-label">Pts Today</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#b794f4' }}>{user.spins_today || 0}</div>
          <div className="stat-label">Spins Today</div>
        </div>
      </div>
      {user.level_data && !user.level_data.is_max_level && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            <span>Level {user.level_data.level}</span>
            <span>{Math.round(user.level_data.progress * 100)}% to Level {user.level_data.level + 1}</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-secondary)', overflow: 'hidden' }}>
            <div style={{
              width: `${Math.round(user.level_data.progress * 100)}%`,
              height: '100%', borderRadius: 3,
              background: 'linear-gradient(90deg, var(--accent), #68d391)',
            }} />
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 8 }}>User Info</h3>
        <div style={{ fontSize: 14, lineHeight: 2, color: 'var(--text-secondary)' }}>
          <div>Admin: <strong style={{ color: 'var(--text-primary)' }}>{user.is_admin ? 'Yes' : 'No'}</strong></div>
          <div>Joined: <strong style={{ color: 'var(--text-primary)' }}>{new Date(user.created_at).toLocaleDateString()}</strong></div>
          <div>Last Login: <strong style={{ color: 'var(--text-primary)' }}>{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}</strong></div>
          <div>Invite Code: <code style={{ color: 'var(--accent)', fontSize: 12 }}>{user.invite_code || '-'}</code></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn-success btn-small" onClick={() => setShowPoints(true)}>Drop Points</button>
          <button className="btn-primary btn-small" onClick={() => setShowSpin(true)}>Gift Toke</button>
          <button className="btn-small" style={{ background: '#d69e2e', color: '#fff' }} onClick={() => setShowGiftCoupon(true)}>Gift Coupon</button>
          <button className="btn-small" style={{ background: '#805ad5', color: '#fff' }} onClick={() => setShowXpAdjust(true)}>Adjust XP</button>
          <button className="btn-secondary btn-small" onClick={() => { setNewUsername(user.username); setShowRename(true); }}>Rename</button>
          <button className="btn-secondary btn-small" onClick={handleResetPassword}>Generate Reset Link</button>
          {!user.is_admin && !user.deleted_at && (
            <button className="btn-danger btn-small" onClick={() => setShowDeleteConfirm(true)}>Delete User</button>
          )}
        </div>
      </div>

      {/* Sales Stats */}
      {salesStats && parseFloat(salesStats.total_orders) > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>Sales Stats</h3>
          <div className="stat-grid" style={{ marginBottom: 0 }}>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 18 }}>{parseFloat(salesStats.total_grams)}g</div>
              <div className="stat-label">Total Purchased</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 18 }}>
                {parseFloat(salesStats.total_revenue).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
              </div>
              <div className="stat-label">Total Spent</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 18, color: 'var(--success)' }}>
                {parseFloat(salesStats.total_profit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
              </div>
              <div className="stat-label">Profit</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 18 }}>{salesStats.total_orders}</div>
              <div className="stat-label">Orders</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 18 }}>{salesStats.avg_order_size}g</div>
              <div className="stat-label">Avg Order</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 18 }}>
                {parseFloat(salesStats.lifetime_value).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
              </div>
              <div className="stat-label">LTV</div>
            </div>
          </div>
          {salesStats.favourite_strain && (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
              Favourite strain: <strong style={{ color: 'var(--success)' }}>{salesStats.favourite_strain}</strong>
            </div>
          )}
        </div>
      )}

      {/* Reset Link Modal */}
      {resetLink && (
        <div className="modal-overlay" onClick={() => setResetLink(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Password Reset Link</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
              This link expires in 30 minutes. Share it with the user.
            </p>
            <div style={{
              background: 'var(--bg-secondary)', padding: '10px', borderRadius: 'var(--radius-sm)',
              fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: 'var(--accent)',
              marginBottom: 12
            }}>
              {resetLink}
            </div>
            <div className="btn-row">
              <button className="btn-primary" onClick={() => copyToClipboard(resetLink)}>Copy Link</button>
              <button className="btn-secondary" onClick={() => setResetLink(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Delete User</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
              Are you sure you want to delete <strong>{user.username}</strong>? This will deactivate their account.
            </p>
            <div className="btn-row">
              <button className="btn-danger" onClick={handleDelete}>Yes, Delete</button>
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Coupons */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Reward Nugs</h3>
        {(user.coupons || []).length === 0 ? (
          <div className="empty-state">No reward nugs</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Reward</th>
                  <th>Status</th>
                  <th className="hide-mobile">Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {user.coupons.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontFamily: 'monospace', color: 'var(--warning)' }}>{c.coupon_code}</td>
                    <td>{c.reward_detail || c.reward_type}</td>
                    <td>
                      <span className={`badge ${c.redeemed_at ? 'badge-redeemed' : 'badge-active'}`}>
                        {c.redeemed_at ? 'Picked Up' : 'Active'}
                      </span>
                    </td>
                    <td className="hide-mobile" style={{ fontSize: 12 }}>{new Date(c.created_at).toLocaleDateString()}</td>
                    <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {!c.redeemed_at && (
                        <>
                          <button className="btn-success btn-small" onClick={() => handleRedeemCoupon(c.id)}>
                            Mark Picked Up
                          </button>
                          <button
                            className="btn-danger btn-small"
                            onClick={() => { setDeleteCouponTarget(c); setShowDeleteCouponConfirm(true); }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>XP History</h3>
        {xpHistory.length === 0 ? (
          <div className="empty-state">No XP activity yet</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>XP</th>
                  <th>Detail</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {xpHistory.slice(0, 30).map(tx => {
                  const { dateLabel, time } = fmtTs(tx.created_at);
                  return (
                  <tr key={tx.id}>
                    <td><span className="badge" style={{ background: 'var(--accent)', color: '#fff', fontSize: 10 }}>{tx.source}</span></td>
                    <td style={{ color: tx.amount > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </td>
                    <td style={{ fontSize: 12 }}>{tx.detail}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'block' }}>{dateLabel}</span>
                      <span style={{ opacity: 0.7 }}>{time}</span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Points History</h3>
        {(user.transactions || []).length === 0 ? (
          <div className="empty-state">No Bud Points activity yet</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Reason</th>
                  <th>Amount</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {user.transactions.map(tx => {
                  const { dateLabel, time } = fmtTs(tx.created_at);
                  return (
                  <tr key={tx.id}>
                    <td>{tx.reason}</td>
                    <td style={{ color: tx.amount > 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'block' }}>{dateLabel}</span>
                      <span style={{ opacity: 0.7 }}>{time}</span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showPoints && (
        <PointsModal user={user} onClose={() => setShowPoints(false)} onDone={() => { setShowPoints(false); fetchUser(); }} />
      )}
      {showSpin && (
        <SpinGrantModal user={user} onClose={() => setShowSpin(false)} onDone={() => { setShowSpin(false); fetchUser(); }} />
      )}

      {showXpAdjust && (
        <div className="modal-overlay" onClick={() => setShowXpAdjust(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Adjust XP for {user.username}</h3>
            <div className="form-group">
              <label>XP Amount (positive to add, negative to remove)</label>
              <input type="number" value={xpAmount} onChange={e => setXpAmount(e.target.value)} autoFocus />
            </div>
            <div className="form-group">
              <label>Reason</label>
              <input value={xpReason} onChange={e => setXpReason(e.target.value)} placeholder="e.g. Manual correction" />
            </div>
            <div className="btn-row">
              <button className="btn-primary" onClick={handleXpAdjust} disabled={!xpAmount || parseInt(xpAmount) === 0 || !xpReason.trim()}>Apply</button>
              <button className="btn-secondary" onClick={() => setShowXpAdjust(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showRename && (
        <div className="modal-overlay" onClick={() => setShowRename(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Rename User</h3>
            <div className="form-group">
              <label>New Username</label>
              <input
                value={newUsername}
                onChange={e => setNewUsername(e.target.value)}
                maxLength={30}
                autoFocus
              />
            </div>
            <div className="btn-row">
              <button className="btn-primary" onClick={handleRename} disabled={!newUsername.trim() || newUsername.trim() === user.username}>Save</button>
              <button className="btn-secondary" onClick={() => setShowRename(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteCouponConfirm && deleteCouponTarget && (
        <div className="modal-overlay" onClick={() => { setShowDeleteCouponConfirm(false); setDeleteCouponTarget(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Delete Coupon</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
              Delete coupon <strong style={{ color: 'var(--warning)', fontFamily: 'monospace' }}>{deleteCouponTarget.coupon_code}</strong>?
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              {deleteCouponTarget.pending_booking_id
                ? 'This coupon is applied to a pending order. It will be unlinked from that order before deletion.'
                : 'This coupon is active and will be permanently deleted.'}
            </p>
            <div className="btn-row">
              <button className="btn-danger" onClick={handleDeleteCoupon}>Delete</button>
              <button className="btn-secondary" onClick={() => { setShowDeleteCouponConfirm(false); setDeleteCouponTarget(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showGiftCoupon && (
        <div className="modal-overlay" onClick={() => setShowGiftCoupon(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Gift Coupon to {user.username}</h3>
            <div className="form-group">
              <label>Reward Detail</label>
              <select
                value={giftCouponDetail}
                onChange={e => setGiftCouponDetail(e.target.value)}
              >
                <option value="1g Nug - Free Pickup">1g Nug - Free Pickup</option>
                <option value="2g Nug - Free Pickup">2g Nug - Free Pickup</option>
                <option value="3.5g Nug - Free Pickup">3.5g Nug - Free Pickup</option>
                <option value="7g Nug - Free Pickup">7g Nug - Free Pickup</option>
                <option value="14g Nug - Free Pickup">14g Nug - Free Pickup</option>
                <option value="Pre-Roll - Free Pickup">Pre-Roll - Free Pickup</option>
              </select>
            </div>
            <div className="btn-row">
              <button className="btn-primary" onClick={async () => {
                try {
                  const data = await api.post(`/admin/users/${id}/gift-coupon`, { reward_detail: giftCouponDetail });
                  showToast('success', `Coupon gifted: ${data.coupon.coupon_code}`);
                  setShowGiftCoupon(false);
                  fetchUser();
                } catch (err) {
                  showToast('error', err.message);
                  setShowGiftCoupon(false);
                }
              }}>Gift Coupon</button>
              <button className="btn-secondary" onClick={() => setShowGiftCoupon(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
